import { Application, Container } from 'pixi.js';

/**
 * Main renderer. Initializes PixiJS, manages the render loop,
 * and provides the root container for scenes.
 */
export class Renderer {
  private app: Application;
  private sceneContainer: Container;
  private _width: number = 0;
  private _height: number = 0;

  constructor() {
    this.app = new Application();
    this.sceneContainer = new Container();
  }

  async init(container: HTMLElement): Promise<void> {
    await this.app.init({
      background: 0x1a1a2e,
      resizeTo: container,
      antialias: false, // Pixel art — no smoothing
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });

    container.appendChild(this.app.canvas);

    this._width = this.app.screen.width;
    this._height = this.app.screen.height;

    this.app.stage.addChild(this.sceneContainer);

    // Handle resize
    const resizeObserver = new ResizeObserver(() => {
      this._width = this.app.screen.width;
      this._height = this.app.screen.height;
    });
    resizeObserver.observe(container);
  }

  /** The root container for the current scene. */
  get stage(): Container {
    return this.sceneContainer;
  }

  /** Current canvas width in CSS pixels. */
  get width(): number {
    return this._width;
  }

  /** Current canvas height in CSS pixels. */
  get height(): number {
    return this._height;
  }

  /** The PixiJS application instance (for ticker access etc). */
  get pixiApp(): Application {
    return this.app;
  }

  /** Add a callback to the render loop. */
  addTickerCallback(fn: (delta: number) => void): void {
    this.app.ticker.add((ticker) => fn(ticker.deltaTime));
  }

  /** Get the canvas element. */
  get canvas(): HTMLCanvasElement {
    return this.app.canvas;
  }

  destroy(): void {
    this.app.destroy(true);
  }
}
